// Takida baslik/etiket denetimleri (Hakan Bey 3 yayin + Noel serisi 2. bolum).
//
// Etiket temizleyici: ayni/cok benzer etiket, tekil-cogul ikizi ve baslikta
// virgulle ayrilmis bir obegi BIREBIR tekrar eden etiket elenir (Erkam: baslikta
// kullanilan obek etikette tekrarlanmaz; etiket ek anahtar kelime icindir).
// Bosalan yerler yedek havuzdan doldurulur.
//
// Baslik uyarilari engellemez; panel kullaniciya gosterir.

const SMALL = new Set(["a", "an", "and", "for", "with", "of", "the", "to", "in", "on", "her", "him"]);

/** Kaba tekillestirme: necklaces -> necklace, charms -> charm (aramada Etsy ikisini esler) */
const stem = (w: string) => (w.length > 4 && w.endsWith("es") && /(ches|shes|sses|xes)$/.test(w) ? w.slice(0, -2) : w.length > 3 && w.endsWith("s") && !w.endsWith("ss") ? w.slice(0, -1) : w);
const words = (s: string) => s.toLowerCase().replace(/[^a-z0-9' ]+/g, " ").split(/\s+/).filter(Boolean);
const key = (s: string) => words(s).map(stem).join(" ");

export function cleanTags(tags: string[], title: string, backup: string[] = [], max = 13): string[] {
  const titleSegments = new Set(title.split(",").map(key).filter(Boolean));
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of [...tags, ...backup]) {
    const t = String(raw ?? "").toLowerCase().replace(/\s+/g, " ").trim();
    if (!t || t.length > 20) continue;
    const k = key(t);
    // Kelime sirasi farkli ayni etiket ("gold name necklace" / "name necklace gold")
    const bag = k.split(" ").sort().join(" ");
    if (!k || seen.has(bag) || titleSegments.has(k)) continue;
    seen.add(bag);
    out.push(t);
    if (out.length === max) break;
  }
  return out;
}

/** Hakan Bey: bu sifatlar baslikta bir kez kullanilir */
const ONCE_ADJECTIVES = ["custom", "personalized", "customized", "dainty", "minimalist", "tiny", "mini", "gold", "silver", "sterling", "plated"];

export function titleWarnings(title: string): string[] {
  const out: string[] = [];
  const ws = words(title);
  for (const a of ONCE_ADJECTIVES) {
    const n = ws.filter((w) => w === a).length;
    if (n > 1) out.push(`"${a}" başlıkta ${n} kez geçiyor — sıfatı bir kez kullan, diğerinde eş anlamlısını yaz.`);
  }
  // Iki kelimelik ayni obegin tekrari ("gold cross ... gold cross")
  const pairs = new Map<string, number>();
  for (let i = 0; i < ws.length - 1; i++) {
    if (SMALL.has(ws[i]) || SMALL.has(ws[i + 1])) continue;
    const p = `${stem(ws[i])} ${stem(ws[i + 1])}`;
    pairs.set(p, (pairs.get(p) ?? 0) + 1);
  }
  for (const [p, n] of pairs) if (n > 1) out.push(`"${p}" öbeği başlıkta ${n} kez tekrar ediyor.`);
  if (ws.includes("stone") || ws.includes("stones")) out.push(`"stone" yerine taşlı üründe CZ ya da pave yaz (Hakan Bey).`);
  if (ws.includes("diamond") || ws.includes("diamonds")) out.push(`"diamond" yanlış bilgi — taşlar CZ (kübik zirkon).`);
  if (/\b(solid|real) (gold|14k|18k|10k)\b/i.test(title)) out.push("Ürün kaplama — \"solid/real gold\" yazılmaz.");
  if (/\b22k\b/i.test(title)) out.push("22K yazılmaz (alıcı 14K/18K'ya alışkın).");

  // Bos / tek basina kalan parca ("..., Necklace, ..."): arama ifadesi degil
  const segs = title.split(",").map((x) => x.trim());
  if (segs.some((x) => !x)) out.push("Başlıkta boş parça var (yan yana iki virgül).");
  const lonely = segs.filter((x) => x && words(x).length === 1 && LONE_WORDS.has(words(x)[0]));
  if (lonely.length) out.push(`"${lonely.join('", "')}" tek başına kalmış — bir arama ifadesinin parçası yap (ör. "Dainty Necklace").`);

  // Ust tema (Hakan Bey: urun -> TEMA -> alici)
  const t = ` ${words(title).join(" ")} `;
  for (const th of THEMES) {
    if (th.when.test(t) && !th.has.test(t)) out.push(`Üst tema eksik: ${th.label} ürününde "${th.suggest}" gibi bir tema ifadesi ekle.`);
  }
  // Son parca alici/durum ifadesi
  const last = segs.filter(Boolean).pop() ?? "";
  if (segs.filter(Boolean).length >= 3 && !/\b(gift|gifts|present|for (her|him|mom|women|men|girls|wife|daughter|sister|grandma)|christmas|birthday|mother'?s day|valentine|anniversary|easter|wedding|bridesmaid|graduation|baptism)\b/i.test(last)) {
    out.push("Son parçada alıcı/hediye ifadesi yok (ör. \"Christmas Gift for Her\").");
  }
  return out;
}

/** Tek basina anlamsiz kalan genel kelimeler */
const LONE_WORDS = new Set(["necklace", "necklaces", "jewelry", "gift", "gifts", "pendant", "bracelet", "earrings", "ring", "charm", "custom", "personalized"]);

/** Urun tipi -> ust tema sozlugu (Hakan Bey 3. yayin ornekleri) */
const THEMES: Array<{ label: string; when: RegExp; has: RegExp; suggest: string }> = [
  { label: "Haç", when: /\bcross(es)?\b/, has: /\b(religious|christian|faith|baptism|communion|confirmation|easter|catholic|god|jesus)\b/, suggest: "Religious Jewelry / Christian Gift / Baptism" },
  { label: "Boynuz (horn)", when: /\b(horn|cornicello)\b/, has: /\b(talisman|protection|evil eye|amulet|lucky|good luck)\b/, suggest: "Talisman / Protection Jewelry" },
  { label: "Nazar", when: /\bevil eye\b/, has: /\b(protection|talisman|amulet|lucky|good luck)\b/, suggest: "Protection / Good Luck Jewelry" },
  { label: "Deniz canlısı", when: /\b(fish|shell|seashell|starfish|turtle|dolphin|wave|octopus|whale)\b/, has: /\b(ocean|sea|beach|nautical|summer|coastal)\b/, suggest: "Ocean / Beach Jewelry" },
  { label: "Böcek/arı", when: /\b(bee|butterfly|dragonfly|ladybug|insect)\b/, has: /\b(insect|nature|garden|bug)\b/, suggest: "Insect / Nature Jewelry" },
  { label: "Ay/güneş/yıldız", when: /\b(moon|sun|star|crescent|constellation)\b/, has: /\b(celestial|astrology|zodiac|galaxy|space)\b/, suggest: "Celestial Jewelry" },
  { label: "Hayvan", when: /\b(cat|dog|paw|horse|bunny|elephant)\b/, has: /\b(pet|animal|lover|memorial)\b/, suggest: "Pet Lover / Animal Jewelry" },
];
